import { createDraftService } from '../utils/client.js';
import { formatOutput, formatSuccess, formatError, formatTable, isInteractiveOutput } from '../utils/format.js';
import { handleApiError, requireOption } from '../utils/errors.js';
import { cliCommentToHtml, htmlToPlainText } from '../../../../src/utils/cli-rich-text.js';

export async function createDraft(
  ticket: string,
  options: { text?: string; comment?: boolean; json?: boolean },
  token?: string,
  apiUrl?: string
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    const json = options?.json;

    const text = options.text;
    if (!text) {
      console.error(formatError('--text is required'));
      process.exit(1);
    }
    requireOption(text, 'text');

    const service = await createDraftService(token, apiUrl);
    const result = await service.createDraft({
      ticket_number: ticket,
      draft_type: options.comment ? 'comment' : 'description',
      text: cliCommentToHtml(text),
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Created ${result.draft.draftType} draft for ${ticket} (draft id: ${result.draft.id})`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function listDrafts(
  ticket: string,
  options: { json?: boolean },
  token?: string,
  apiUrl?: string
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    const json = options?.json;

    const service = await createDraftService(token, apiUrl);
    const result = await service.listDrafts({ ticket_number: ticket });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      if (result.drafts && result.drafts.length > 0) {
        if (isInteractiveOutput()) {
          console.log(`\nDraft(s) for ${ticket}:`);
        }
        const rows = result.drafts.map((d: any) => [
          d.id,
          d.draftType,
          htmlToPlainText(String(d.text ?? '')),
        ]);
        const termWidth = process.stdout.columns || 120;
        const textWidth = Math.max(50, termWidth - 38);
        console.log(formatTable(['ID', 'Type', 'Text'], rows, [undefined, undefined, textWidth]));
      } else {
        console.log('No drafts found');
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function updateDraft(
  draftId: number,
  options: { text?: string; json?: boolean },
  token?: string,
  apiUrl?: string
): Promise<void> {
  try {
    requireOption(draftId.toString(), 'draft-id');
    const json = options?.json;

    const text = options.text;
    if (!text) {
      console.error(formatError('--text is required'));
      process.exit(1);
    }
    requireOption(text, 'text');

    const service = await createDraftService(token, apiUrl);
    const result = await service.updateDraft({
      draft_id: draftId,
      text: cliCommentToHtml(text),
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Updated draft ${draftId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function publishDraft(
  draftId: number,
  options: { json?: boolean },
  token?: string,
  apiUrl?: string
): Promise<void> {
  try {
    requireOption(draftId.toString(), 'draft-id');
    const json = options?.json;

    const service = await createDraftService(token, apiUrl);
    const result = await service.publishDraft({ draft_id: draftId });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Published draft ${draftId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function deleteDraft(
  draftId: number,
  options: { json?: boolean },
  token?: string,
  apiUrl?: string
): Promise<void> {
  try {
    requireOption(draftId.toString(), 'draft-id');
    const json = options?.json;

    const service = await createDraftService(token, apiUrl);
    const result = await service.deleteDraft({ draft_id: draftId });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Deleted draft ${draftId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}
